define((require) => {
    "use strict";

    const PolymorphicCollection                         = require("shared/zbone/PolymorphicCollection");
    const IntegrationModel                              = require("editor/ui/settings/integrations/IntegrationModel");
    const Preconditions                                 = require("shared/util/Preconditions");
    const AmazonRekognitionIntegrationModel             = require("editor/ui/settings/integrations/AmazonRekognitionIntegrationModel");
    const NativeContentIntegrationModel                 = require("editor/ui/settings/integrations/NativeContentIntegrationModel");
    const ThumbnailsNativeContentIntegrationModel       = require("editor/ui/settings/integrations/ThumbnailsNativeContentIntegrationModel");
    const ThumbnailsImageDashboardIntegrationModel      = require("editor/ui/settings/integrations/ThumbnailsImageDashboardIntegrationModel");
    const MatomoReportingDashboardLinkVisibleModel      = require("editor/ui/settings/integrations/MatomoReportingDashboardLinkVisibleIntegrationModel");
    const EventCollectionUsesMatomoModel                = require("editor/ui/settings/integrations/EventCollectionUsesMatomoIntegrationModel");
    const FontIntegrationModel                          = require("editor/ui/settings/integrations/FontIntegrationModel");
    const CustomFontIntegrationModel                    = require("editor/ui/settings/integrations/CustomFontIntegrationModel");
    const TypekitIntegrationModel                       = require("editor/ui/settings/integrations/TypekitIntegrationModel");
    const FontsDotComIntegrationModel                   = require("editor/ui/settings/integrations/FontsDotComIntegrationModel");
    const Scene7IntegrationModel                        = require("editor/ui/settings/integrations/Scene7IntegrationModel");
    const InventoryTaggingModel                         = require("editor/ui/settings/integrations/InventoryTaggingModel");
    const ZSitesModel                                   = require("editor/ui/settings/integrations/ZSitesModel");
    const RestApi                                       = require("shared/services/RestApi");

    /**
     * A collection of IntegrationModels.
     *
     * @see IntegrationModel
     *
     * @class IntegrationCollection
     */
    const IntegrationCollection = PolymorphicCollection.extend("IntegrationCollection", {

        companyId: null,
        defaultModelType: IntegrationModel,

        constructor(attrs, options) {
            this.__super(attrs, options);

            if (!(options && options.companyId)) {
                Preconditions.fail("options.companyId has to be defined.");
            }
            this.companyId = options.companyId;
            this.options = options;
        },

        modelTypes: () => ({
            [CustomFontIntegrationModel.type]: CustomFontIntegrationModel,
            [TypekitIntegrationModel.type]: TypekitIntegrationModel,
            [FontsDotComIntegrationModel.type]: FontsDotComIntegrationModel,
            [AmazonRekognitionIntegrationModel.type]: AmazonRekognitionIntegrationModel,
            [Scene7IntegrationModel.type]: Scene7IntegrationModel,
            [NativeContentIntegrationModel.type]: NativeContentIntegrationModel,
            [ThumbnailsNativeContentIntegrationModel.type]: ThumbnailsNativeContentIntegrationModel,
            [ThumbnailsImageDashboardIntegrationModel.type]: ThumbnailsImageDashboardIntegrationModel,
            [MatomoReportingDashboardLinkVisibleModel.type]: MatomoReportingDashboardLinkVisibleModel,
            [EventCollectionUsesMatomoModel.type]: EventCollectionUsesMatomoModel,
            [InventoryTaggingModel.type]: InventoryTaggingModel,
            [ZSitesModel.type]: ZSitesModel
        }),

        url() {
            // "/api/companies/{companyId}/integrations"
            return RestApi.pathToCollectionOnResource(RestApi.Collections.COMPANIES, this.companyId, RestApi.Collections.INTEGRATIONS);
        },

        byClass(integrationClass) {
            return this.filter((integration) => {
                return integration instanceof integrationClass;
            });
        },

        getIntegration(integrationClass) {
            return this.byClass(integrationClass)[0];
        },

        fontIntegrationsEnabled() {
            return !!this.fontIntegrations().length;
        },

        imageRekognitionIntegrationsEnabled() {
            return this.getIntegration(AmazonRekognitionIntegrationModel) !== undefined;
        },

        isNativeContentIntegrationsEnabled() {
            return this.getIntegration(NativeContentIntegrationModel) !== undefined;
        },

        isThumbnailsImageIntegrationsEnabled() {
            return this.getIntegration(ThumbnailsImageDashboardIntegrationModel) !== undefined;
        },

        isThumbnailsNativeContentIntegrationsEnabled() {
            return this.getIntegration(ThumbnailsNativeContentIntegrationModel) !== undefined;
        },

        isMatomoReportingDashboardLinkVisibleIntegrationsEnabled() {
            return this.getIntegration(MatomoReportingDashboardLinkVisibleModel) !== undefined;
        },

        isEventCollectionUsesMatomoIntegrationsEnabled() {
            return this.getIntegration(EventCollectionUsesMatomoModel) !== undefined;
        },

        isInventoryEnabled() {
            return this.getIntegration(InventoryTaggingModel) !== undefined;
        },

        isZSitesEnabled() {
            return this.getIntegration(ZSitesModel) !== undefined;
        },

        /**
         * Checks if the integration is authorized, meaning that client entered correct credentials in the settings.
         * We only check username because the backend API does not return a password, but we know that a username is set only if credentials were correct.
         *
         * @returns {boolean} True if the integration is authorized.
         */
        isScene7IntegrationAuthorized() {
            const scene7Integration = this.getIntegration(Scene7IntegrationModel);
            return Boolean(scene7Integration && scene7Integration.get("username"));
        },

        /**
         * Checks if the integration has been enabled for the client. Currently is only possible from Zelp.
         *
         * @returns {boolean} True if the integration is enabled.
         */
        isScene7IntegrationEnabled() {
            return this.getIntegration(Scene7IntegrationModel) !== undefined;
        },

        getImageSourceTypeServingUrl(sourceTypeName) {
            const sourceTypeIntegration = this.modelTypes()[sourceTypeName];
            const sourceTypeModel = sourceTypeIntegration && this.find((model) => model instanceof sourceTypeIntegration);
            return sourceTypeModel && sourceTypeModel.get("imageServingUrl");
        },

        /**
         * @return {FontIntegrationModel[]}
         */
        fontIntegrations() {
            return this.byClass(FontIntegrationModel);
        }
    });

    return IntegrationCollection;
});



// WEBPACK FOOTER //
// ./src/editor/ui/settings/integrations/IntegrationCollection.js